import { publicIdSchema } from "@rosc/contracts";
import { create } from "zustand";
import { env } from "@/env";
import axiosClientV2 from "@/lib/axios/axiosClientV2";
import { getVisitScope } from "@/lib/visit-queries";

// One audio engine for a visit page (v3.2): the transcript drawer's player,
// tapping a segment and "Replay this moment" in Sources all drive the same
// <audio> element, so two recordings never play over each other. The
// recording is fetched once per account and audio route (the route needs
// the session) and kept as an object URL; its waveform peaks are computed
// once with Web Audio and cached.

export const PLAYBACK_RATES = [1, 1.25, 1.5, 2] as const;
export const SKIP_SECONDS = 10;
const PEAKS = 96;
const AUDIO_PATH =
  /^\/api\/v2\/visits\/([^/]+)\/(?:recordings\/([^/]+)\/)?audio\/content$/;

const blobs = new Map<string, Promise<{ url: string; blob: Blob }>>();
const peaksCache = new Map<string, Promise<number[] | null>>();

const isNotFound = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 404;

export const validAudioPath = (path: string): boolean => {
  try {
    const url = new URL(path, env.VITE_API_V2_BASE_URL);
    const match = AUDIO_PATH.exec(url.pathname);
    return (
      url.origin === new URL(env.VITE_API_V2_BASE_URL).origin &&
      !!match &&
      publicIdSchema.safeParse(match[1]).success &&
      (match[2] === undefined || publicIdSchema.safeParse(match[2]).success) &&
      !url.search &&
      !url.hash
    );
  } catch {
    return false;
  }
};

export const loadRecording = (
  path: string,
  uid: string,
): Promise<{ url: string; blob: Blob }> => {
  const key = `${uid}:${path}`;
  const cached = blobs.get(key);
  if (cached) return cached;
  if (!validAudioPath(path))
    return Promise.reject(new Error("Invalid media source"));
  const href = new URL(path, env.VITE_API_V2_BASE_URL).href;
  const loading = axiosClientV2
    .get<Blob>(href, { responseType: "blob", expectedUserUid: uid })
    .then((response) => ({
      url: URL.createObjectURL(response.data),
      blob: response.data,
    }));
  blobs.set(key, loading);
  loading.catch(() => blobs.delete(key));
  return loading;
};

// Stories and tests: a recording already in hand (no network).
export const primeRecording = (path: string, uid: string, blob: Blob): void => {
  blobs.set(
    `${uid}:${path}`,
    Promise.resolve({ url: URL.createObjectURL(blob), blob }),
  );
};

// Normalised peaks (0..1) for the scrubber's mini waveform; null when the
// browser cannot decode the recording (the scrubber is then a plain bar).
export const recordingPeaks = (
  path: string,
  uid: string,
): Promise<number[] | null> => {
  const key = `${uid}:${path}`;
  const cached = peaksCache.get(key);
  if (cached) return cached;
  const computing = loadRecording(path, uid)
    .then(async ({ blob }) => {
      const Context =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Context) return null;
      const context = new Context();
      try {
        const buffer = await context.decodeAudioData(await blob.arrayBuffer());
        return peaksOf(buffer.getChannelData(0), PEAKS);
      } finally {
        void context.close();
      }
    })
    .catch(() => null);
  peaksCache.set(key, computing);
  return computing;
};

export const peaksOf = (samples: Float32Array, count: number): number[] => {
  const size = Math.max(1, Math.floor(samples.length / count));
  const peaks: number[] = [];
  for (let bucket = 0; bucket < count; bucket++) {
    let max = 0;
    const start = bucket * size;
    for (let i = start; i < Math.min(samples.length, start + size); i++) {
      const value = Math.abs(samples[i] ?? 0);
      if (value > max) max = value;
    }
    peaks.push(max);
  }
  const top = Math.max(...peaks, 0.0001);
  return peaks.map((value) => Math.max(0.06, value / top));
};

type EngineState = {
  // The audio route loaded in the element.
  path: string | null;
  // "missing": the server has no audio for this recording (404), which is
  // not a load failure (shown as "No audio", without Try again).
  status: "idle" | "loading" | "ready" | "error" | "missing";
  playing: boolean;
  currentTime: number;
  duration: number;
  rate: (typeof PLAYBACK_RATES)[number];
  // While set, playback stops at this time (a replayed moment).
  stopAt: number | null;
};

let element: HTMLAudioElement | null = null;

export const useAudioEngine = create<
  EngineState & {
    load: (path: string) => Promise<boolean>;
    play: () => Promise<void>;
    pause: () => void;
    toggle: () => Promise<void>;
    seek: (seconds: number) => void;
    skip: (seconds: number) => void;
    setRate: (rate: (typeof PLAYBACK_RATES)[number]) => void;
    cycleRate: () => void;
    // Plays [startMs, endMs] of a recording (segment tap, Replay).
    playRange: (
      path: string,
      startMs: number,
      endMs: number | null,
    ) => Promise<boolean>;
    reset: () => void;
  }
>()((set, get) => {
  const audio = (): HTMLAudioElement => {
    if (element) return element;
    element = new Audio();
    element.preload = "auto";
    element.ontimeupdate = () => {
      const now = element?.currentTime ?? 0;
      const { stopAt } = get();
      if (stopAt !== null && now >= stopAt) {
        element?.pause();
        set({ stopAt: null });
      }
      set({ currentTime: now });
    };
    element.ondurationchange = () => {
      const value = element?.duration ?? 0;
      set({ duration: Number.isFinite(value) ? value : 0 });
    };
    element.onplay = () => set({ playing: true });
    element.onpause = () => set({ playing: false });
    element.onended = () => set({ playing: false, stopAt: null });
    return element;
  };
  return {
    path: null,
    status: "idle",
    playing: false,
    currentTime: 0,
    duration: 0,
    rate: 1,
    stopAt: null,
    load: async (path) => {
      const scope = getVisitScope();
      if (!scope) return false;
      if (get().path === path && get().status === "ready") return true;
      const player = audio();
      player.pause();
      set({
        path,
        status: "loading",
        playing: false,
        currentTime: 0,
        duration: 0,
        stopAt: null,
      });
      try {
        const { url } = await loadRecording(path, scope.uid);
        if (get().path !== path) return false;
        player.src = url;
        player.playbackRate = get().rate;
        set({ status: "ready" });
        return true;
      } catch (error) {
        if (get().path === path)
          set({ status: isNotFound(error) ? "missing" : "error" });
        return false;
      }
    },
    play: async () => {
      const player = audio();
      if (get().status !== "ready") return;
      try {
        await player.play();
      } catch {
        set({ playing: false });
      }
    },
    pause: () => {
      element?.pause();
    },
    toggle: async () => {
      if (get().playing) get().pause();
      else {
        set({ stopAt: null });
        await get().play();
      }
    },
    seek: (seconds) => {
      const player = audio();
      const end = get().duration || Number.POSITIVE_INFINITY;
      player.currentTime = Math.min(Math.max(0, seconds), end);
      set({ currentTime: player.currentTime, stopAt: null });
    },
    skip: (seconds) => {
      get().seek((element?.currentTime ?? 0) + seconds);
    },
    setRate: (rate) => {
      if (element) element.playbackRate = rate;
      set({ rate });
    },
    cycleRate: () => {
      const index = PLAYBACK_RATES.indexOf(get().rate);
      get().setRate(PLAYBACK_RATES[(index + 1) % PLAYBACK_RATES.length] ?? 1);
    },
    playRange: async (path, startMs, endMs) => {
      if (!(await get().load(path))) return false;
      const player = audio();
      // A little before and after, so the first and last words are whole.
      player.currentTime = Math.max(0, startMs - 250) / 1000;
      set({
        currentTime: player.currentTime,
        stopAt: endMs === null ? null : (endMs + 400) / 1000,
      });
      await get().play();
      return true;
    },
    reset: () => {
      element?.pause();
      set({
        path: null,
        status: "idle",
        playing: false,
        currentTime: 0,
        duration: 0,
        stopAt: null,
      });
    },
  };
});
